// src/App.js
import React, { useEffect } from 'react';
import {
  Box, Button, CircularProgress,
  AppBar, Toolbar, Typography, IconButton, Drawer, CssBaseline
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import { FormProvider, useFormContext } from './context/FormContext';
import { useAuth } from "react-oidc-context";
import { setUser } from "./authManager";
import { performSignOut } from "./signOut";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Components
import Sidebar from './components/Sidebar';
import SaveButton from './components/SaveButton';
import ScrollToTopButton from './components/ScrollToTopButton';

// Pages
import HomePage from './components/HomePage';
import CBCTReport from './components/CBCTReport';
import ImpactionReport from './components/ImpactionReport';
import AdminDashboard from './components/AdminDashboard';
import PanoramicMarker from './components/PanoramicMarker';

const drawerWidth = 340;

// Layout Component containing AppBar and Sidebar
function Layout({ children, onSidebarToggle }) {
  const { state } = useFormContext();
  const auth = useAuth();
  const location = useLocation();

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${state.isSidebarOpen ? drawerWidth : 0}px)`,
          ml: state.isSidebarOpen ? `${drawerWidth}px` : 0,
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={onSidebarToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ORAL-D 3D CBCT SCAN REPORT
          </Typography>
          {location.pathname === '/admin' && (
            <Button color="inherit" href="/" startIcon={<HomeIcon />}>Home</Button>
          )}
          <Button color="inherit" onClick={() => performSignOut()}>Sign out</Button>
          <SaveButton />
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={state.isSidebarOpen}
      >
        <Sidebar />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          transition: (theme) => theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: `-${drawerWidth}px`,
          ...(state.isSidebarOpen && {
            transition: (theme) => theme.transitions.create('margin', {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
          }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
      <ScrollToTopButton />
    </Box>
  );
}

// Admin Route Wrapper
function AdminRoute({ children }) {
  const auth = useAuth();
  const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === 'true';

  if (auth.isLoading) return <div>Loading...</div>;

  if (!auth.isAuthenticated && !bypassAuth) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is in 'admin' group
  const userGroups = auth.user?.profile?.['cognito:groups'] || [];
  const isAdmin = userGroups.includes('admin') || bypassAuth;

  if (!isAdmin) {
    // If user is not admin, redirect to home
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const auth = useAuth();
  const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === 'true';

  // Enforce login if auth is not bypassed
  useEffect(() => {
    if (!auth.isLoading && !auth.error && !auth.isAuthenticated && !bypassAuth && !auth.activeNavigator) {
      auth.signinRedirect();
    }
  }, [auth, bypassAuth]);

  if (auth.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (auth.error) {
    return <div>Encountering error... {auth.error.message}</div>;
  }


  if (!auth.isAuthenticated && !bypassAuth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Redirecting to login...</Typography>
      </Box>
    );
  }

  if (auth.user) {
    setUser(auth.user);
  }

  return (
    <BrowserRouter>
      <FormProvider>
        <AppRoutes />
      </FormProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { actions } = useFormContext();

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/implant" element={<CBCTReport />} />
      <Route path="/impaction" element={
        <AdminRoute>
          <ImpactionReport />
        </AdminRoute>
      } />
      <Route path="/marker" element={<PanoramicMarker />} />
      <Route path="/admin" element={
        <AdminRoute>
          {/* Admin doesn't necessarily need Sidebar, but let's keep Layout for consistency/AppBar */}
          <Layout onSidebarToggle={() => actions.toggleSidebar()}>
            <AdminDashboard />
          </Layout>
        </AdminRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
